import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { format, parseISO, startOfMonth } from 'date-fns';
import { useQuery } from 'react-query';
import CardDemonstration from 'src/components/CardDemonstration/CardDemonstration';
import Subtitle from 'src/components/Subtitle';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { useFicFidcDashboardDate } from 'src/context/FicFidcDashboardDateContext';
import {
  type FicChildTradesHistoryPeriod,
  type FicChildTradesHistoryResponse,
  getMetaCarteirasFicFidcsChildren,
  getMetaCarteirasFicFidcsTradesHistory,
  type MetaCarteirasFicFidcsChildrenResponse,
} from 'src/services/meta-carteiras-fic-fidcs';

import { FundMovementsChart } from './FundMovementsChart';
import { FundMovementsDetailedView } from './FundMovementsDetailedView';
import {
  ALL_PERIOD_SELECTION,
  formatCompetenceMonthLabel,
  getAvailableCompetencesFromMovements,
  getAvailableMovementCompetences,
  mapChildrenToFundMovements,
  normalizeCompetenceDate,
} from './mapChildrenToFundMovements';

import type {
  FundMovementsExperienceProps,
  FundMovementsPeriodSelection,
  FundMovementsResponse,
  FundMovementsViewMode,
} from './types';

export function getLatestAvailableCompetence(
  tradesHistoryData?: FicChildTradesHistoryResponse | null,
  childrenData?: MetaCarteirasFicFidcsChildrenResponse | null,
): string | null {
  const dates: string[] = [];
  if (tradesHistoryData?.meta?.endCompetence) {
    dates.push(tradesHistoryData.meta.endCompetence);
  }
  (childrenData?.trades?.data ?? []).forEach((t) => {
    if (t.dtComptc) dates.push(t.dtComptc);
  });
  (childrenData?.positions?.data ?? []).forEach((p) => {
    if (p.dtComptc) dates.push(p.dtComptc);
  });
  if (childrenData?.positions?.meta?.baseMonth) {
    dates.push(childrenData.positions.meta.baseMonth);
  }
  if (dates.length === 0) return null;
  dates.sort((a, b) => b.localeCompare(a));
  return dates[0];
}

export function FundMovementsExperience({
  operationId,
  initialCompetenceDate,
  title = 'Movimentação da carteira',
  showHeader = true,
  viewMode: viewModeProp,
  onViewModeChange,
  childrenData: childrenDataProp,
  isLoadingChildren: isLoadingChildrenProp,
  isErrorChildren: isErrorChildrenProp,
  tradesHistoryData: tradesHistoryDataProp,
  data,
  dataProp: dataPropAlias,
  hasPermission = true,
}: FundMovementsExperienceProps) {
  const dataProp = data !== undefined ? data : dataPropAlias;
  const authContext = useAuthContext();
  // Fora do dashboard FIC/FIDC (ex.: Monitor) não há provider: modo legado,
  // sem `date`, preservando chamadas e query keys originais.
  const dashboardDate = useFicFidcDashboardDate();
  const dashboardDateValue = dashboardDate?.date ?? null;
  const setDashboardDate = dashboardDate?.setDate;
  const plansToUpgrade = authContext?.upgradedPlanByPermission
    ? (authContext.upgradedPlanByPermission(
        'operacoes_fidic_ativo_carteira',
      ) as unknown as string | undefined)
    : undefined;

  const [internalViewMode, setInternalViewMode] =
    useState<FundMovementsViewMode>('chart');
  const viewMode = viewModeProp ?? internalViewMode;

  const [chartPeriod, setChartPeriod] =
    useState<FicChildTradesHistoryPeriod>('12m');

  const [selectedPeriod, setSelectedPeriod] =
    useState<FundMovementsPeriodSelection>(ALL_PERIOD_SELECTION);
  const prevViewModeRef = useRef<FundMovementsViewMode>(viewMode);
  const isBarClickRef = useRef(false);

  const {
    data: fetchedHistoryData,
    isLoading: isLoadingHistory,
    isError: isErrorHistory,
    error: historyError,
  } = useQuery(
    dashboardDateValue
      ? ['fund-movements-history', operationId, chartPeriod, dashboardDateValue]
      : ['fund-movements-history', operationId, chartPeriod],
    () =>
      dashboardDateValue
        ? getMetaCarteirasFicFidcsTradesHistory(
            operationId,
            chartPeriod,
            dashboardDateValue,
          )
        : getMetaCarteirasFicFidcsTradesHistory(operationId, chartPeriod),
    {
      enabled:
        !!operationId &&
        tradesHistoryDataProp == null &&
        dataProp == null &&
        hasPermission,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const isChildrenControlledByParent =
    childrenDataProp !== undefined || isLoadingChildrenProp !== undefined;

  const shouldFetchChildren =
    !!operationId &&
    !isChildrenControlledByParent &&
    dataProp === undefined &&
    hasPermission;

  const {
    data: fetchedChildrenData,
    isLoading: isLoadingFetchedChildren,
    isError: isErrorFetchedChildren,
    error: childrenError,
  } = useQuery(
    dashboardDateValue
      ? ['fund-movements-children', operationId, dashboardDateValue]
      : ['fund-movements-children', operationId],
    () =>
      dashboardDateValue
        ? getMetaCarteirasFicFidcsChildren(operationId, {
            date: dashboardDateValue,
          })
        : getMetaCarteirasFicFidcsChildren(operationId),
    {
      enabled: shouldFetchChildren,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const effectiveHistoryData =
    useMemo<FicChildTradesHistoryResponse | null>(() => {
      if (tradesHistoryDataProp) return tradesHistoryDataProp;
      if (dataProp) {
        const competence = `${dataProp.referenceMonth}-01`;
        return {
          data: [
            {
              competence,
              purchases: dataProp.summary.purchases.totalValue,
              sales: dataProp.summary.sales.totalValue,
              net: dataProp.summary.net.totalValue,
            },
          ],
          meta: {
            period: chartPeriod,
            startCompetence: competence,
            endCompetence: competence,
            totals: {
              purchases: dataProp.summary.purchases.totalValue,
              sales: dataProp.summary.sales.totalValue,
              net: dataProp.summary.net.totalValue,
            },
          },
        };
      }
      return fetchedHistoryData ?? null;
    }, [tradesHistoryDataProp, dataProp, fetchedHistoryData, chartPeriod]);

  const effectiveChildrenData = isChildrenControlledByParent
    ? childrenDataProp ?? null
    : fetchedChildrenData ?? null;
  const isLoadingChildren = isChildrenControlledByParent
    ? isLoadingChildrenProp ?? false
    : isLoadingFetchedChildren;
  const isErrorChildren = isChildrenControlledByParent
    ? isErrorChildrenProp ?? false
    : isErrorFetchedChildren;

  const availableCompetences = useMemo(() => {
    if (effectiveChildrenData) {
      return getAvailableMovementCompetences(effectiveChildrenData);
    }
    if (dataProp) {
      return getAvailableCompetencesFromMovements(dataProp.movements);
    }
    return [];
  }, [dataProp, effectiveChildrenData]);

  useEffect(() => {
    setSelectedPeriod(ALL_PERIOD_SELECTION);
    isBarClickRef.current = false;
  }, [operationId]);

  useEffect(() => {
    if (prevViewModeRef.current !== viewMode) {
      if (viewMode === 'detailed' && !isBarClickRef.current) {
        setSelectedPeriod(ALL_PERIOD_SELECTION);
        setDashboardDate?.(null);
      }
      isBarClickRef.current = false;
      prevViewModeRef.current = viewMode;
    }
  }, [setDashboardDate, viewMode]);

  useEffect(() => {
    if (
      selectedPeriod !== ALL_PERIOD_SELECTION &&
      availableCompetences.length > 0 &&
      !availableCompetences.includes(selectedPeriod)
    ) {
      setSelectedPeriod(ALL_PERIOD_SELECTION);
      setDashboardDate?.(null);
    }
  }, [availableCompetences, selectedPeriod, setDashboardDate]);

  const detailedData = useMemo<FundMovementsResponse | null>(() => {
    if (dataProp) {
      if (selectedPeriod === ALL_PERIOD_SELECTION) {
        return {
          ...dataProp,
          referenceMonth: ALL_PERIOD_SELECTION,
          referenceMonthLabel: 'Todo período',
        };
      }
      return {
        ...dataProp,
        referenceMonth: format(
          startOfMonth(parseISO(selectedPeriod)),
          'yyyy-MM',
        ),
        referenceMonthLabel: formatCompetenceMonthLabel(selectedPeriod),
      };
    }
    if (!effectiveChildrenData) return null;
    return mapChildrenToFundMovements(
      operationId,
      selectedPeriod,
      effectiveChildrenData,
    );
  }, [dataProp, effectiveChildrenData, operationId, selectedPeriod]);

  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: FundMovementsViewMode | null,
  ) => {
    if (newMode !== null) {
      if (newMode === 'detailed') {
        setSelectedPeriod(ALL_PERIOD_SELECTION);
        setDashboardDate?.(null);
      }
      setInternalViewMode(newMode);
      onViewModeChange?.(newMode);
    }
  };

  const handleDetailedPeriodChange = (period: FundMovementsPeriodSelection) => {
    // "Todo período" restaura o modo legado do dashboard (sem date); um mês
    // selecionado define a competência autoritativa, normalizada.
    setDashboardDate?.(
      period === ALL_PERIOD_SELECTION ? null : normalizeCompetenceDate(period),
    );
    setSelectedPeriod(period);
  };

  const handleChartCompetenceSelect = (competence: string) => {
    const normalizedCompetence = normalizeCompetenceDate(competence);
    const hasDetailedDataLoaded =
      effectiveChildrenData != null || dataProp != null;

    if (
      !hasDetailedDataLoaded ||
      !availableCompetences.includes(normalizedCompetence)
    ) {
      return;
    }

    isBarClickRef.current = true;
    setDashboardDate?.(normalizedCompetence);
    setSelectedPeriod(normalizedCompetence);
    setInternalViewMode('detailed');
    onViewModeChange?.('detailed');
  };

  const chartErrorMessage =
    historyError instanceof Error
      ? historyError.message
      : 'Não foi possível carregar o histórico de movimentações.';

  const detailedErrorMessage =
    childrenError instanceof Error
      ? childrenError.message
      : 'Não foi possível carregar as movimentações.';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        position: 'relative',
      }}>
      {showHeader && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}>
          <Subtitle title={title} />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            aria-label="Alternar visualização">
            <ToggleButton
              value="chart"
              aria-label="Gráfico"
              sx={{
                textTransform: 'none',
                px: 2,
                fontWeight: viewMode === 'chart' ? 700 : 500,
              }}>
              Gráfico
            </ToggleButton>
            <ToggleButton
              value="detailed"
              aria-label="Visão detalhada"
              sx={{
                textTransform: 'none',
                px: 2,
                fontWeight: viewMode === 'detailed' ? 700 : 500,
              }}>
              Visão detalhada
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {!hasPermission ? (
        <Box
          sx={{
            position: 'relative',
            minHeight: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}>
          <CardDemonstration
            title={`Recurso disponível apenas no plano ${plansToUpgrade || 'PRO'}`}
            subTitle="Faça um upgrade em sua assinatura para ter acesso às movimentações da carteira."
            labelButton="Fazer upgrade"
            url="/planos"
          />
        </Box>
      ) : viewMode === 'chart' ? (
        <FundMovementsChart
          data={effectiveHistoryData}
          period={chartPeriod}
          onPeriodChange={setChartPeriod}
          onCompetenceSelect={handleChartCompetenceSelect}
          isLoading={isLoadingHistory && tradesHistoryDataProp == null}
          isError={isErrorHistory && tradesHistoryDataProp == null}
          errorMessage={chartErrorMessage}
        />
      ) : (
        <FundMovementsDetailedView
          data={detailedData}
          selectedPeriod={selectedPeriod}
          availableCompetences={availableCompetences}
          onPeriodChange={handleDetailedPeriodChange}
          isLoading={isLoadingChildren && childrenDataProp == null}
          isError={isErrorChildren && childrenDataProp == null}
          errorMessage={detailedErrorMessage}
        />
      )}
    </Box>
  );
}

export default FundMovementsExperience;
